import { Alert, List, Text } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import {
  TravelActivityType,
  TravelExpense,
  TravelRequestIssue,
  travelFileKindLabel,
} from '../model/TravelRequestTypes';
import { BlockingRequestLink, useBlockingRequestDetail } from './TravelErasmusBlocking';

interface TravelRequestSubmitHintsProps {
  issues: TravelRequestIssue[];
  activityType: TravelActivityType;
  expenses: Pick<TravelExpense, 'expenseType'>[];
}

export function TravelRequestSubmitHints({ issues, activityType, expenses }: TravelRequestSubmitHintsProps) {
  const { t } = useTranslation();
  const blockingDetail = useBlockingRequestDetail();

  if (issues.length === 0) {
    return null;
  }

  const subject = (issue: TravelRequestIssue) => {
    if (issue.fileKind != null) {
      return travelFileKindLabel(t, issue.fileKind, activityType);
    }
    const label = issue.field == null ? '' : t(`travel.requests.form.fields.${issue.field}`);
    if (issue.expenseIndex == null) {
      return label;
    }
    const isSupport = (index: number) => expenses[index]?.expenseType === 'UMFST_MOBILITY_SUPPORT';
    if (isSupport(issue.expenseIndex)) {
      const section = t('travel.requests.form.sections.mobilitySupport');
      return label === '' ? section : `${section} - ${label}`;
    }
    // The support line has its own card, so the expenses card numbers only the rows around it.
    const supportRowsBefore = expenses.slice(0, issue.expenseIndex).filter((_, index) => isSupport(index)).length;
    return `${label} #${issue.expenseIndex + 1 - supportRowsBefore}`;
  };

  return (
    <Alert
      color="yellow"
      icon={<IconAlertTriangle size={18} />}
      title={t('travel.requests.issues.title')}
    >
      <List size="sm">
        {issues.map(issue => {
          const blockingRule = issue.rule === 'earlierReturnPending' ? issue.rule : 'erasmusCertificatePending';

          return (
            <List.Item
              key={`${issue.rule}:${issue.field}:${issue.expenseIndex}:${issue.fileKind}:${issue.blockingRequest?.id}:${issue.values?.year}`}
            >
              {issue.blockingRequest == null ? (
                <Text
                  span
                  size="sm"
                  fw="500"
                >
                  {subject(issue)}
                </Text>
              ) : (
                <BlockingRequestLink
                  request={issue.blockingRequest}
                  rule={blockingRule}
                />
              )}{' '}
              <Text
                span
                size="sm"
              >
                {issue.blockingRequest == null
                  ? t(`travel.requests.issues.${issue.rule}`, issue.values)
                  : blockingDetail(issue.blockingRequest, blockingRule)}
              </Text>
            </List.Item>
          );
        })}
      </List>
    </Alert>
  );
}
